import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from './apiService';
import {
  UserData,
  UserUpdateRequest,
  PasswordChangeRequest,
  ApiResponse,
  BankData,
  AgencyData,
  MarketData,
  EventPartData,
  PopupData,
  QuestionRequest,
  QuestionListRequest,
  QuestionItem,
  PartnerLinkersRequest,
  PartnerLinker,
} from '../types/api';

// User Authentication Services
export async function login(credentials: {
  loginId: string;
  loginPwd: string;
}): Promise<ApiResponse<{ token: string; user: UserData; adminToken?: string; popups?: PopupData[] }>> {
  try {
    const payload = await apiService.addCommonData(credentials);
    const response = await apiService.post('/api/Users/login', payload);
    
    if (response.status === 1) {
      throw new Error(response.message.warning || 'Login failed');
    }
    
    if (response.data?.token) {
      apiService.setToken(response.data.token);
    }
    
    if (response.data?.user) {
      apiService.storeUserData(response.data.user);
    }
    
    return response;
  } catch (error) {
    throw error;
  }
}

export async function logout(): Promise<ApiResponse> {
  try {
    const payload = await apiService.addCommonData({
      adminToken: localStorage.getItem('authToken'),
    });
    const response = await apiService.post('/api/Users/logout', payload);
    apiService.clearAllData();
    return response;
  } catch (error) {
    // Clear data even if logout fails
    apiService.clearAllData();
    throw error;
  }
}

export async function getUserInfo(): Promise<ApiResponse<any>> {
  try {
    const response = await apiService.postRaw('/api/Users/getMyData',{}, {
      headers: {
        'Authorization': localStorage.getItem('authToken'),
      },
    });
    
    if (response.status) {
      throw new Error(response.message.warning || 'Failed to get user info');
    }
    if (response) {
      apiService.storeAllUserData(response);
    }
    
    if (response?.user) {
      apiService.storeUserData(response.user);
    }
    
    // Notify header and other listeners so balance updates (they read from getStoredAllUserData)
    window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
    
    return response;
  } catch (error) {
    throw error;
  }
}

// User Management Services
export async function updateUserInfo(data: UserUpdateRequest): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/updateUserInfo', data, {
      'Authorization': localStorage.getItem('authToken'),
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function changePassword(data: PasswordChangeRequest): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/passwordChange', data, {
      headers: {
        'Authorization': localStorage.getItem('authToken'),
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
}

// Bank and Agency Data Services
export async function getBankData(): Promise<ApiResponse<{ bankList: BankData[] }>> {
  try {
    const response = await apiService.post('/api/Users/getBankData');
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getAgencyData(): Promise<ApiResponse<{ agencyList: AgencyData[] }>> {
  try {
    const response = await apiService.post('/api/Users/getAgencyData');
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getMarketData(): Promise<{ data: MarketData[] }> {
  try {
    const response = await apiService.postRaw('/api/Users/getMarketData');
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getEventPartData(): Promise<{ data: EventPartData[] }> {
  try {
    const response = await apiService.postRaw('/api/Users/getEventPartData');
    console.log(response);
    return response;
  } catch (error) {
    throw error;
  }
}

// Popup Services
export async function getPublicPopup(): Promise<ApiResponse<{ popups: PopupData[] }>> {
  try {
    const response = await apiService.post('/api/Popup/get', {});
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getPrivatePopup(): Promise<ApiResponse<{ popups: PopupData[] }>> {
  try {
    const response = await apiService.post('/api/Popup/getpopup', {});
    return response;
  } catch (error) {
    throw error;
  }
}

export async function hidePopup(id: string): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/popup/hide', { id });
    return response;
  } catch (error) {
    throw error;
  }
}

// Question/Support Services
export async function sendQuestion(data: QuestionRequest): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Question/send', data);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getQuestionList(params: QuestionListRequest): Promise<ApiResponse<{ data: QuestionItem[] }>> {
  try {
    const response = await apiService.post('/api/Question/getList', params);
    return response;
  } catch (error) {
    throw error;
  }
}

// Partner Services
export async function getPartnerLinkers(params: PartnerLinkersRequest): Promise<ApiResponse<{ data: PartnerLinker[] }>> {
  try {
    const response = await apiService.post('/api/Users/getPartnerLinkers', params);
    return response;
  } catch (error) {
    throw error;
  }
}

// React Query Hooks
export function useLogin() {
  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if (data.data?.token) {
        apiService.setToken(data.data.token);
      }
      if (data.data?.user) {
        apiService.storeUserData(data.data.user);
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      apiService.clearAllData();
    },
    onError: () => {
      // Clear data even if logout fails
      apiService.clearAllData();
    },
  });
}

export function useUserInfo() {
  return useQuery({
    queryKey: ['user', 'info'],
    queryFn: getUserInfo,
    enabled: apiService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

export function useUpdateUserInfo() {
  return useMutation({
    mutationFn: updateUserInfo,
    onSuccess: () => {
      // Refetch user info after update
      // You might want to invalidate the user info query here
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
  });
}

export function useBankData() {
  return useQuery({
    queryKey: ['user', 'bankData'],
    queryFn: getBankData,
    enabled: apiService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useAgencyData() {
  return useQuery({
    queryKey: ['user', 'agencyData'],
    queryFn: getAgencyData,
    enabled: apiService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useMarketData() {
  return useQuery({
    queryKey: ['user', 'marketData'],
    queryFn: getMarketData,
    enabled: apiService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useEventPartData() {
  return useQuery({
    queryKey: ['user', 'eventPartData'],
    queryFn: getEventPartData,
    enabled: apiService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function usePublicPopup() {
  return useQuery({
    queryKey: ['popup', 'public'],
    queryFn: getPublicPopup,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

export function usePrivatePopup() {
  return useQuery({
    queryKey: ['popup', 'private'],
    queryFn: getPrivatePopup,
    enabled: apiService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

export function useQuestionList(params: QuestionListRequest) {
  return useQuery({
    queryKey: ['question', 'list', params],
    queryFn: () => getQuestionList(params),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSendQuestion() {
  return useMutation({
    mutationFn: sendQuestion,
  });
}

export function usePartnerLinkers(params: PartnerLinkersRequest) {
  return useQuery({
    queryKey: ['partner', 'linkers', params],
    queryFn: () => getPartnerLinkers(params),
    enabled: apiService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

// Additional User Services from Old Project
export async function signUp(params: any): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/signUp', params);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function checkLoginId(loginId: string): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/checkLoginId', { loginId });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function checkEmail(email: string): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/checkEmail', { email });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getAdminInfo(): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Users/getAdminInfo', {});
    return response;
  } catch (error) {
    throw error;
  }
}

export function useSignUp() {
  return useMutation({
    mutationFn: signUp,
  });
}

export function useCheckLoginId() {
  return useMutation({
    mutationFn: checkLoginId,
  });
}

export function useAdminInfo() {
  return useQuery({
    queryKey: ['user', 'adminInfo'],
    queryFn: getAdminInfo,
    enabled: apiService.isAuthenticated(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}